/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../config/api';
import logger from '../utils/logger';

const AIAssistantContext = createContext(null);

export function AIAssistantProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem('acuity_muted');
    return saved === 'true';
  });
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('acuity_volume');
    return saved ? parseFloat(saved) : 0.7;
  });
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem('asp_token');
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!localStorage.getItem('asp_token')) return;
    try {
      const response = await api.get('/assistant/settings', { requireAuth: true });
      if (response.success) {
        setSettings(response.settings);
        if (!response.settings.onboarding_completed) {
          setShowOnboarding(true);
        }
      }
    } catch (error) {
      // Silently fail if not authenticated
      if (!error.message?.includes('401')) {
        logger.error('Failed to fetch assistant settings:', error);
      }
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!localStorage.getItem('asp_token')) return;
    try {
      const response = await api.get('/assistant/history?limit=50', { requireAuth: true });
      if (response.success) {
        setMessages(response.messages || []);
      }
    } catch (error) {
      // Silently fail if not authenticated
      if (!error.message?.includes('401')) {
        logger.error('Failed to fetch history:', error);
      }
    }
  }, []);

  // Initialize on mount if authenticated
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      if (localStorage.getItem('asp_token') && !isInitialized && mounted) {
        await fetchSettings();
        await fetchHistory();
        if (mounted) {
          setIsInitialized(true);
        }
      }
    };
    
    init();
    
    return () => { mounted = false; };
  }, [fetchSettings, fetchHistory, isInitialized]);

  // Listen for login/logout events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'asp_token') {
        if (e.newValue) {
          // User logged in
          fetchSettings();
          fetchHistory();
          setIsInitialized(true);
        } else {
          // User logged out
          setSettings(null);
          setMessages([]);
          setIsInitialized(false);
          setShowOnboarding(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchSettings, fetchHistory]);

  const updateSettings = async (updates) => {
    try {
      const response = await api.put('/assistant/settings', updates, { requireAuth: true });
      if (response.success) {
        setSettings(response.settings);
        return response.settings;
      }
    } catch (error) {
      logger.error('Failed to update settings:', error);
    }
    return null;
  };

  const sendMessage = useCallback(async (message, currentPage = 'dashboard') => {
    if (!message.trim() || isLoading || !localStorage.getItem('asp_token')) return null;

    // Add user message immediately
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const response = await api.post('/assistant/chat', {
        message,
        current_page: currentPage
      }, { requireAuth: true });

      if (response.success) {
        const assistantMsg = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          actions: response.actions || []
        };
        setMessages(prev => [...prev, assistantMsg]);

        // Handle actions
        if (response.actions && response.actions.length > 0) {
          setPendingAction(response.actions[0]);
        }

        // Speak response if voice enabled
        if (settings?.voice_enabled) {
          speak(response.response);
        }

        return response;
      }
    } catch (error) {
      logger.error('Failed to send message:', error);
      const errorMsg = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an issue. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [isLoading, settings?.voice_enabled]);

  const executeAction = useCallback(async (action, confirmed = false) => {
    try {
      const response = await api.post('/assistant/action', {
        action,
        confirmed
      }, { requireAuth: true });

      if (response.requires_confirmation) {
        return { needsConfirmation: true, message: response.message };
      }

      setPendingAction(null);
      
      // Handle successful action execution
      if (response.success && response.result) {
        const result = response.result;
        
        // Handle navigation action
        if (result.type === 'navigate' && result.page) {
          // Navigation will be handled by the widget component
          return { success: true, navigate: result.page, result };
        }
        
        // Handle draft email action - just return the data for display
        if (result.type === 'draft') {
          return { success: true, draft: result.data, result };
        }
        
        return { success: true, result };
      }
      
      return response;
    } catch (error) {
      logger.error('Failed to execute action:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Save mute/volume to localStorage when changed
  useEffect(() => {
    localStorage.setItem('acuity_muted', isMuted.toString());
  }, [isMuted]);

  useEffect(() => {
    localStorage.setItem('acuity_volume', volume.toString());
  }, [volume]);

  const speak = useCallback(async (text) => {
    if (!settings?.voice_enabled || !text || isMuted) return;

    try {
      setIsSpeaking(true);
      // Shorten text for TTS - limit to 200 chars for faster loading
      const shortenedText = text.length > 200 ? text.substring(0, 197) + '...' : text;
      
      const response = await api.post('/assistant/voice/synthesize', {
        text: shortenedText,
        voice: settings?.voice || 'nova',
        speed: settings?.voice_speed || 1.0
      }, { requireAuth: true });

      if (response.success && response.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.audio}`);
        audio.volume = volume;
        audioRef.current = audio;
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => setIsSpeaking(false);
        await audio.play();
      } else {
        setIsSpeaking(false);
      }
    } catch (error) {
      logger.error('Speech synthesis failed:', error);
      setIsSpeaking(false);
    }
  }, [settings, isMuted, volume]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  const startListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      logger.error('Speech recognition not supported');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (e) => {
      logger.error('Speech recognition error:', e);
      setIsListening(false);
    };
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        sendMessage(transcript);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [sendMessage]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, []);

  const clearHistory = useCallback(async () => {
    try {
      await api.delete('/assistant/history', { requireAuth: true });
      setMessages([]);
    } catch (error) {
      logger.error('Failed to clear history:', error);
    }
  }, []);

  const completeOnboarding = useCallback(async (name, personality, voice) => {
    await updateSettings({
      assistant_name: name,
      personality,
      voice,
      onboarding_completed: true
    });
    setShowOnboarding(false);
  }, []);

  // Initialize assistant (call after login)
  const initializeAssistant = useCallback(() => {
    if (isAuthenticated() && !isInitialized) {
      fetchSettings();
      fetchHistory();
      setIsInitialized(true);
    }
  }, [isInitialized]);

  const value = {
    // State
    isOpen,
    isMinimized,
    settings,
    messages,
    isLoading,
    isSpeaking,
    isListening,
    pendingAction,
    showOnboarding,
    isInitialized,
    isMuted,
    volume,
    // Actions
    setIsOpen,
    setIsMinimized,
    sendMessage,
    executeAction,
    speak,
    stopSpeaking,
    startListening,
    stopListening,
    updateSettings,
    clearHistory,
    completeOnboarding,
    setPendingAction,
    setShowOnboarding,
    fetchSettings,
    fetchHistory,
    initializeAssistant,
    setIsMuted,
    setVolume
  };

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}

export function useAIAssistant() {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider');
  }
  return context;
}
